import * as React from "react";

import { cn } from "@/lib/utils";

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto rounded-2xl border border-black/5 bg-(--card-background) shadow-2xl backdrop-blur-lg dark:border-white/10 dark:bg-white/5"
    >
      <table
        data-slot="table"
        className={cn(
          "w-full min-w-[800px] caption-bottom text-sm text-left text-(--foreground) border-separate border-spacing-0",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "bg-linear-to-r from-(--primary) via-(--secondary) to-(--primary) text-(--foreground) [&_tr]:border-b-0 [&_tr:first-child_th:first-child]:rounded-tl-xl [&_tr:first-child_th:last-child]:rounded-tr-xl",
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        "[&_tr:nth-child(odd)]:bg-transparent [&_tr]:border-b [&_tr]:border-black/5 [&_tr]:transition-colors [&_tr]:duration-200 text-(--foreground) dark:[&_tr]:border-white/5 [&_tr:nth-child(even)]:bg-(--foreground)/10",
        className,
      )}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-[var(--background-muted-secondary)]/60 border-t border-black/5 font-medium text-[var(--foreground)] dark:border-white/5 [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b border-transparent transition-colors duration-200 hover:bg-[var(--background-muted-secondary)]/70  dark:hover:bg-white/5",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-14 px-3 md:px-5 text-left align-middle font-semibold text-[0.65rem] md:text-[0.75rem] uppercase tracking-[0.12em] md:tracking-[0.18em] text-(--secondary-foreground) whitespace-nowrap first:rounded-l-xl last:rounded-r-xl [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-3 md:px-5 py-3 md:py-4 align-middle text-xs md:text-sm text-[var(--foreground)] opacity-90 whitespace-nowrap first:rounded-l-xl last:rounded-r-xl [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
